import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { PanelLeft } from 'lucide-react';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Overlay from '../common/Overlay';
import Logo from './Logo';
import MobileMenuCloseButton from './MobileMenuCloseButton';
import SidenavMenu from './SidenavMenu';

type Props = {
  sidenavState: OpenClosedStates;
  setSidenavState: Dispatch<SetStateAction<OpenClosedStates>>;
  hide: boolean;
  user: any;
};

const COLLAPSE_KEY = 'admin.sidenavCollapsed';

const Sidenav: React.FC<Props> = ({ sidenavState, setSidenavState, hide }) => {
  const [showSidenav, setShowSidenav] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(
    () => window.localStorage.getItem(COLLAPSE_KEY) === 'true'
  );

  useEffect(() => {
    setShowSidenav(sidenavState === OpenClosedStates.Open);
  }, [sidenavState]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      window.localStorage.setItem(COLLAPSE_KEY, String(next));
      return next;
    });
  };

  return (
    <>
      <div className="lg:hidden print:hidden" hidden={sidenavState === OpenClosedStates.Closed}>
        <div className="fixed inset-0 flex z-40">
          <Overlay show={showSidenav} opacity={90} />
          <Transition
            show={showSidenav}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full pt-3 pb-4 bg-gray-50">
              <MobileMenuCloseButton
                sidenavState={sidenavState}
                setSidenavState={setSidenavState}
              />
              <div className="px-4">
                <Logo />
              </div>
              <div className="mt-5 flex-1 h-0 overflow-y-auto">
                <nav className="px-2">
                  <SidenavMenu />
                </nav>
              </div>
            </div>
          </Transition>
          <div className="shrink-0 w-14" />
        </div>
      </div>

      <div
        className={clsx(
          hide ? 'hidden' : 'hidden lg:flex lg:shrink-0 bg-white dark:bg-dark-gray-900'
        )}
      >
        <div
          className={clsx(
            'relative flex flex-col border-r border-gray-300 dark:border-dark-gray-700 print:hidden',
            'transition-[width] duration-200 ease-out',
            collapsed ? 'w-16' : 'w-64'
          )}
        >
          <div
            className={clsx(
              'h-0 flex-1 flex flex-col',
              collapsed ? 'overflow-visible' : 'overflow-y-auto'
            )}
          >
            <div className="mt-5 grow flex flex-col">
              <nav className="flex-1 px-2">
                <SidenavMenu isCollapsed={collapsed} />
              </nav>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute top-4 -right-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm transition-[color,background-color,border-color,box-shadow] duration-200 hover:bg-gray-50 hover:shadow-md dark:border-dark-gray-600 dark:bg-dark-gray-700 dark:hover:bg-dark-gray-600"
          >
            <PanelLeft
              className={clsx(
                'h-4 w-4 text-gray-500 transition-transform duration-300 dark:text-gray-400',
                collapsed && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidenav;
